import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Library, Check, X, PackageOpen, ChevronDown, ChevronRight,
  Sparkles, ListChecks, ShieldCheck,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * TemplateLibraryDialog
 *
 * kind: "vv" | "checklists"
 *  - "vv"        → catalog of Verification & Validation items
 *  - "checklists"→ catalog of Audit / Checklist templates
 *
 * Fetches the curated catalog, lets the admin pick one, several, or all,
 * then bulk-installs them into their org. Calls `onInstalled(installed[])`
 * after a successful install so the parent page can refresh.
 */
export default function TemplateLibraryDialog({ open, onClose, kind, onInstalled }) {
  const isVV = kind === "vv";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("all");
  const [orgStandards, setOrgStandards] = useState([]);
  const [showUntagged, setShowUntagged] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set()); setExpanded(new Set()); setCategory("all");
    const url = isVV ? "/template-library/vv" : "/template-library/checklists";
    api.get(url).then((r) => setItems(r.data || [])).catch(() => toast.error("Failed to load library"));
    api.get("/orgs/me").then((r) => setOrgStandards(r.data.org?.active_standards || [])).catch(() => {});
  }, [open, isVV]);

  const categories = useMemo(() => {
    const cats = new Set(items.map((i) => i.category));
    return ["all", ...[...cats].sort()];
  }, [items]);

  const visible = useMemo(() => {
    let list = items;
    if (orgStandards.length > 0) {
      list = list.filter((i) => {
        const stds = i.standards || [];
        if (stds.length === 0) return showUntagged;
        return stds.some((s) => orgStandards.includes(s));
      });
    }
    if (category !== "all") list = list.filter((i) => i.category === category);
    return list;
  }, [items, category, orgStandards, showUntagged]);

  const toggle = (k) => setSelected((s) => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const toggleExpand = (k) => setExpanded((s) => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const selectAllVisible = () => setSelected(new Set(visible.map((v) => v.key)));
  const clearAll = () => setSelected(new Set());

  const install = async () => {
    if (selected.size === 0) { toast.error("Pick at least one template"); return; }
    setBusy(true);
    try {
      const url = isVV ? "/template-library/vv/install" : "/template-library/checklists/install";
      const r = await api.post(url, { keys: [...selected] });
      toast.success(`Installed ${r.data.count} ${isVV ? "V&V item(s)" : "checklist template(s)"}`);
      onInstalled?.(r.data.installed || []);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally {
      setBusy(false);
    }
  };

  const Icon = isVV ? ShieldCheck : ListChecks;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Library className="w-5 h-5 text-[#ff4500]" />
            {isVV ? "V&V Template Library" : "Checklist Template Library"}
          </DialogTitle>
          <DialogDescription className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 pt-1">
            Curated ISO / SQF / GMP presets — pick one, several, or all.
          </DialogDescription>
        </DialogHeader>

        {/* Standards filter banner */}
        {orgStandards.length > 0 && (
          <div className="flex items-center gap-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 flex-wrap">
            <span className="font-bold text-[#ff4500]">// Org filter:</span>
            {orgStandards.map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded bg-[#ff4500]/12 text-[#ff4500] font-bold">{s.toUpperCase()}</span>
            ))}
            <button data-testid="tpl-lib-toggle-untagged" onClick={() => setShowUntagged((v) => !v)}
              className={`px-2 py-0.5 rounded font-bold ${
                showUntagged ? "bg-[#1a1a1a]/8 text-[#1a1a1a]/70" : "border border-[#1a1a1a]/15 text-[#1a1a1a]/45 line-through"
              }`}>
              {showUntagged ? "Untagged ON" : "Untagged OFF"}
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 py-2 border-b border-[#1a1a1a]/10">
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="tpl-lib-category"
            className="text-xs border border-[#1a1a1a]/20 rounded-full px-3 py-1 bg-white">
            {categories.map((c) => (
              <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>
            ))}
          </select>
          <button onClick={selectAllVisible} data-testid="tpl-lib-select-all"
            className="btn-pill btn-pill-ghost text-[11px]">
            <Sparkles className="w-3 h-3" /> Select all shown
          </button>
          {selected.size > 0 && (
            <button onClick={clearAll} data-testid="tpl-lib-clear"
              className="btn-pill btn-pill-ghost text-[11px]">
              <X className="w-3 h-3" /> Clear ({selected.size})
            </button>
          )}
          <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
            {selected.size} / {items.length} selected
          </div>
        </div>

        {/* List */}
        <div className="max-h-96 overflow-auto space-y-2 py-2">
          {visible.length === 0 && (
            <div className="text-center py-8 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
              Loading…
            </div>
          )}
          {visible.map((t) => {
            const on = selected.has(t.key);
            const isExpanded = expanded.has(t.key);
            return (
              <div key={t.key}
                data-testid={`tpl-lib-item-${t.key}`}
                className={`rounded-xl border transition-all ${on ? "border-[#ff4500] bg-[#fff7f2]" : "border-[#1a1a1a]/10 bg-white"}`}>
                <div className="p-3 flex items-start gap-3">
                  <button onClick={() => toggle(t.key)}
                    data-testid={`tpl-lib-toggle-${t.key}`}
                    className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${on ? "bg-[#ff4500] border-[#ff4500]" : "border-[#1a1a1a]/30 bg-white"}`}>
                    {on && <Check className="w-3.5 h-3.5 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Icon className="w-3.5 h-3.5 text-[#a13ad6]" />
                      <span className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/60">{t.category}</span>
                    </div>
                    <div className="font-display font-bold text-sm text-[#1a1a1a]">
                      {isVV ? t.title : t.name}
                    </div>
                    <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5 line-clamp-2">{t.description}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
                      {isVV ? (
                        <>
                          <span className="px-1.5 py-0.5 bg-[#1a1a1a]/8 rounded-full text-[#1a1a1a]/70">{t.schedule_type}</span>
                          <span className="text-[#1a5fb4]">V: {(t.sub_tasks || []).filter((s) => s.section === "verification").length}</span>
                          <span className="text-[#a13ad6]">V&apos;: {(t.sub_tasks || []).filter((s) => s.section === "validation").length}</span>
                        </>
                      ) : (
                        <>
                          <span className="px-1.5 py-0.5 bg-[#1a1a1a]/8 rounded-full text-[#1a1a1a]/70">{t.item_count} items</span>
                          {(t.tags || []).map((tg) => (
                            <span key={tg} className="px-1.5 py-0.5 bg-[#ff4500]/10 rounded-full text-[#ff4500]">#{tg}</span>
                          ))}
                        </>
                      )}
                      <button onClick={() => toggleExpand(t.key)}
                        data-testid={`tpl-lib-expand-${t.key}`}
                        className="ml-auto text-[10px] text-[#1a1a1a]/60 hover:text-[#ff4500] inline-flex items-center gap-1">
                        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Preview
                      </button>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 ml-8 border-t border-[#1a1a1a]/06 pt-2 space-y-1">
                    {isVV ? (
                      (t.sub_tasks || []).map((s, i) => (
                        <div key={i} className="text-[11px] flex items-start gap-2">
                          <span className="font-mono text-[9px] uppercase tracking-wider mt-0.5 px-1 py-0 rounded"
                            style={{ background: s.section === "verification" ? "#1a5fb418" : "#a13ad618",
                                    color: s.section === "verification" ? "#1a5fb4" : "#a13ad6" }}>
                            {s.section === "verification" ? "V" : "V'"}
                          </span>
                          <span className="text-[#1a1a1a]/80">{s.title}</span>
                        </div>
                      ))
                    ) : (
                      (t.items || []).map((it, i) => (
                        <div key={i} className="text-[11px] flex items-start gap-2">
                          <span className="font-mono text-[9px] uppercase tracking-wider mt-0.5 px-1 py-0 rounded bg-[#1a1a1a]/8 text-[#1a1a1a]/70">
                            {it.kind === "passfail" ? "P/F" : it.kind === "written" ? "Text" : it.kind === "rank" ? "1-5" : "MC"}
                          </span>
                          <span className="text-[#1a1a1a]/80">{it.title}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex gap-2 pt-3 border-t border-[#1a1a1a]/10">
          <button onClick={onClose} className="btn-pill btn-pill-ghost" data-testid="tpl-lib-cancel">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button onClick={install} disabled={busy || selected.size === 0}
            data-testid="tpl-lib-install"
            className="btn-pill btn-pill-primary disabled:opacity-50">
            <PackageOpen className="w-3.5 h-3.5" />
            {busy ? "Installing…" : `Install ${selected.size} ${isVV ? "V&V" : "template"}${selected.size === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
